import type { ZuoraPaymentMethod } from '@kimuno/api-sdk';
import { borderRadius, palette, spaces } from '@kimuno/theme';
import { Box, Button, CircularProgress } from '@mui/material';
import config from 'config';
import type React from 'react';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { addError } from '../../actions/errors.actions';
import { updatePaymentMethod } from '../../actions/user.actions';
import { selectUser } from '../../reducers/user.selectors';
import { NotificationContext } from '../../state/NotificationContextProvider';
import { UserPaymentMethodType } from '../../types/User';
import type { ZuoraPaymentPageResponse } from '../../types/ZuoraPayments';
import { getPaymentMethodData } from '../../utils/payment';
import useAction from '../../utils/useAction';
import PaymentContent from './PaymentContent';

interface PaymentMethodsFormProps {
	selectedMethodType: string;
	onPaymentMethodUpdated: () => void;
}

const PaymentMethodsForm = ({
	selectedMethodType,
	onPaymentMethodUpdated,
}: PaymentMethodsFormProps) => {
	const { t } = useTranslation();

	const updatePaymentMethodAction = useAction(updatePaymentMethod);
	const { showNotification } = useContext(NotificationContext);

	const user = useSelector(selectUser);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const onClientSidePaymentErrorHandler = () => {
		setIsLoading(false);
	};

	const onResponse = () => {
		setIsLoading(false);
		showNotification({
			message: t('All changes were saved successfully'),
			severity: 'success',
		});
		onPaymentMethodUpdated();
	};

	const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setIsLoading(true);

		window.Z.submit();
	};

	const onResponseHandler = (
		paymentMethod: ZuoraPaymentMethod,
		response: ZuoraPaymentPageResponse
	) => {
		if (!user) return setIsLoading(false);

		if (!response.success) {
			addError('Error while setting up payment method, please refresh');
			return setIsLoading(false);
		}

		const paymentData = {
			type: paymentMethod,
			paymentMethodId: response.refId,
			braintreeToken: response.auth,
			licenceId: user.licenceId,
		};

		if (!paymentData) return setIsLoading(false);

		const paymentMethodId = paymentData?.paymentMethodId || paymentData?.braintreeToken;

		if (!paymentMethodId) return setIsLoading(false);

		const newPaymentMethod = getPaymentMethodData(paymentData?.type, paymentMethodId);

		updatePaymentMethodAction(newPaymentMethod).finally(onResponse);
	};

	if (!user) {
		return <></>;
	}

	const idButtonHidden =
		selectedMethodType !== UserPaymentMethodType.SEPA &&
		selectedMethodType !== UserPaymentMethodType.CREDIT_CARD;

	return (
		<Box component="form" onSubmit={onSubmit}>
			<PaymentContent
				selectedMethod={selectedMethodType}
				onResponseHandler={onResponseHandler}
				onClientSideErrorHandler={onClientSidePaymentErrorHandler}
				zuoraHostedPagesSource={config.zuoraSinglePageUpgradePaymentSource}
			/>
			<Button
				data-testid="payment-form-submit"
				type="submit"
				variant="contained"
				size="large"
				hidden={idButtonHidden}
				disabled={isLoading}
				sx={{
					backgroundColor: palette.primary.main,
					color: palette.common.white,
					borderRadius: borderRadius.sm,
					padding: `${spaces.xs} ${spaces.lg}`,
				}}
			>
				{isLoading ? (
					<CircularProgress
						size={26}
						sx={{
							color: palette.common.white,
							'& .MuiCircularProgress-svg': {
								height: 'auto',
								width: 'auto',
							},
						}}
					/>
				) : (
					t('Continue to confirmation')
				)}
			</Button>
		</Box>
	);
};

export default PaymentMethodsForm;
